import { Box } from '@mui/material';
import { getRequestEvaluations } from 'lib/api';
import AlertBanner from 'lib/designSystem/AlertBanner';
import { Evaluation } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { buildRequestId } from '../utils';
import EvaluationHistoryDialog from './EvaluationHistoryDialog';
import RiskCard from './RiskCard';
import RiskCardSkeleton from './RiskCardSkeleton';

interface RiskReviewsProps {
  item: any;
  requestTypes: string[];
}

const RiskReviews: React.FC<RiskReviewsProps> = ({ item, requestTypes }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [riskDataArray, setRiskDataArray] = useState<
    Record<string, Evaluation[]>[]
  >([]);
  const [selectedRiskData, setSelectedRiskData] = useState<Evaluation[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const getRiskEvaluations = async () => {
    setLoading(true);
    try {
      const promises = requestTypes.map(async (requestType) => {
        try {
          const response = await getRequestEvaluations(dispatch, {
            requestId: buildRequestId(item, requestType, 'quote'),
            requestType,
          });
          return response;
        } catch (error) {
          console.error(`Error fetching ${requestType}:`, error);
          return {};
        }
      });

      const results = await Promise.all(promises);

      setRiskDataArray(results);
    } catch (error) {
      console.error('Error fetching risk evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (item && requestTypes.length > 0) {
      void getRiskEvaluations();
    }
  }, [item, requestTypes]);

  const handleViewHistory = (riskData: Evaluation[]) => {
    setSelectedRiskData(riskData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRiskData([]);
  };

  if (loading)
    return (
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
        {[1, 2].map((index) => (
          <RiskCardSkeleton key={index} />
        ))}
      </Box>
    );

  const hasAnyEvaluation = riskDataArray.every((data) =>
    Object.values(data).every((evaluations) => evaluations.length === 0),
  );

  if (hasAnyEvaluation)
    return (
      <AlertBanner
        severity="warning"
        title="Motor de riesgo no ejecutado"
        message="Para obtener una evaluación completa del riesgo, es necesario ejecutar el motor de decisión automatizado. Este proceso analizará múltiples factores de riesgo y proporcionará una evaluación detallada. Una vez ejecutado, podrás ver el historial completo de evaluaciones y los detalles de cada análisis de riesgo."
      />
    );

  return (
    <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
      {riskDataArray.map((riskRecord, riskIndex) => {
        return Object.values(riskRecord).map((riskData, dataIndex) => (
          <RiskCard
            key={`${riskIndex}-${dataIndex}`}
            riskData={riskData}
            onViewHistory={() => handleViewHistory(riskData)}
          />
        ));
      })}

      <EvaluationHistoryDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        riskData={selectedRiskData}
      />
    </Box>
  );
};

export default RiskReviews;
